import { computed, ref } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { GlobalVariable } from 'shared/src'
import { useToast } from '@ai/ui-components/toast'
import { BULK_DELETE_BATCH_SIZE } from '@/constants/documents.ts'
import {
  useDeleteVariableMutation,
  useDeleteVariablesBulkMutation,
} from '@/composables/queries/variables'

export const useVariableDelete = (selectedKeys: Ref<(string | number)[]>) => {
  const { t } = useI18n()
  const toast = useToast()
  const deleteVariableMutation = useDeleteVariableMutation()
  const bulkDeleteMutation = useDeleteVariablesBulkMutation()

  const openDeleteModal = ref(false)
  const selectedForDelete = ref<GlobalVariable>()
  const isBulkDeleting = ref(false)
  const bulkDeleteRequestedCount = ref(0)

  const toggleDeleteModal = (item: GlobalVariable) => {
    selectedForDelete.value = item
    openDeleteModal.value = true
  }

  const openBulkDeleteModal = () => {
    selectedForDelete.value = undefined
    openDeleteModal.value = true
  }

  const onDeleteVariable = async (item?: GlobalVariable): Promise<boolean> => {
    const variable = item ?? selectedForDelete.value
    if (!variable || deleteVariableMutation.isPending.value) return false

    try {
      await deleteVariableMutation.mutateAsync(variable.id)
      toast.success(t('item_has_been_deleted', [variable.name]))
      selectedKeys.value = selectedKeys.value.filter((key) => key !== variable.id)
      openDeleteModal.value = false
      return true
    } catch {
      openDeleteModal.value = false
      return false
    }
  }

  const onBulkDeleteVariables = async (ids: string[]) => {
    if (ids.length === 0) return false

    try {
      const result = await bulkDeleteMutation.mutateAsync(ids)

      if (result.ok) {
        toast.success(t('variables_deleted_successfully', { count: ids.length }))
        selectedKeys.value = []
        openDeleteModal.value = false
        return true
      }

      const failedIds = result.failedIds
      const allFailed = failedIds.length === ids.length && failedIds.length > 0

      if (!allFailed && failedIds.length > 0) {
        toast.error(t('some_variables_not_deleted'), { title: t('error_occurred') })
        selectedKeys.value = failedIds
        openDeleteModal.value = false
        return false
      }

      toast.error(t('error_occurred_try_again'), { title: t('error_occurred') })
      return false
    } catch {
      toast.error(t('error_occurred_try_again'), { title: t('error_occurred') })
      return false
    }
  }

  const deleteModalItem = computed(() => {
    if (selectedForDelete.value) return selectedForDelete.value.name || ''
    if (selectedKeys.value.length === 1) {
      return t('one_selected_variable')
    }
    return t('n_selected_variables', [selectedKeys.value.length])
  })

  const onConfirmDelete = async () => {
    if (selectedForDelete.value) {
      return onDeleteVariable(selectedForDelete.value)
    }

    const ids = selectedKeys.value.map(String)
    openDeleteModal.value = false
    bulkDeleteRequestedCount.value = ids.length
    isBulkDeleting.value = true

    try {
      return await onBulkDeleteVariables(ids)
    } finally {
      isBulkDeleting.value = false
      bulkDeleteRequestedCount.value = 0
    }
  }

  const showBulkDeleteLoader = computed(
    () => isBulkDeleting.value && bulkDeleteRequestedCount.value > BULK_DELETE_BATCH_SIZE,
  )

  return {
    openDeleteModal,
    selectedForDelete,
    showBulkDeleteLoader,
    deleteModalItem,
    toggleDeleteModal,
    openBulkDeleteModal,
    onDeleteVariable,
    onBulkDeleteVariables,
    onConfirmDelete,
  }
}
